import { useEffect, useRef } from 'react';

import { type GlobalEventCallback, type GlobalEventTarget } from '@pkg/types';
import { filterNullishValuesFromObject } from '@pkg/utils';

import { useIsoLayoutEffect } from '../useIsoLayoutEffect';

import { type EventListenerHookOptions, type SupportedEventListenerOptions } from './types';

const DEFAULT_OPTIONS: Required<EventListenerHookOptions> = {
    disabled: false,
    preferLayoutEffect: false,
};

const DEFAULT_LISTENER_OPTIONS: SupportedEventListenerOptions = {};

export function useEventListener(
    target: GlobalEventTarget,
    eventType: string,
    callback: GlobalEventCallback,
    options?: EventListenerHookOptions,
    listenerOptions?: SupportedEventListenerOptions,
): void {
    const { disabled, preferLayoutEffect } = {
        ...DEFAULT_OPTIONS,
        ...filterNullishValuesFromObject(options),
    };

    const { capture, passive, once } = {
        ...DEFAULT_LISTENER_OPTIONS,
        ...filterNullishValuesFromObject(),
    };

    const callbackRef = useRef(callback);

    const usePreferredEffect = preferLayoutEffect ? useIsoLayoutEffect : useEffect;

    useIsoLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    usePreferredEffect(() => {
        const handleCallback: GlobalEventCallback = (event) => callbackRef.current(event);

        if (!disabled && target) {
            target.addEventListener(eventType, handleCallback, listenerOptions);
        }

        return () => {
            target?.removeEventListener(eventType, handleCallback, listenerOptions);
        };
    }, [eventType, target, disabled, capture, passive, once]);
}
